import { FileScan, FlaskConical, Loader2, Puzzle, RotateCcw, Settings2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { HealthDot } from '@/components/extensions/HealthDot'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import type { HealthStatus } from '@/services/api/extensions'

/** Deep-agent run caps — the four numeric limits edited together. */
export interface CapsDraft {
  step_cap: number
  wall_clock_seconds: number
  max_tokens: number
  max_findings: number
}

/** One globally-enabled extension row: user-intent toggle + service health. */
export interface ExtensionRow {
  id: string
  name: string
  description: string
  /** User intent — default-on, so a missing setting is `true`. */
  checked: boolean
  /** Service reachability; `'unknown'` while the health probe is pending. */
  status: HealthStatus | 'unknown'
}

export interface CaseSettingsViewProps {
  /** Whether settings/schemas are still loading. */
  loading: boolean
  /** True while a settings mutation is in flight. */
  saving: boolean
  /** True while the destructive reset is in flight. */
  resetting: boolean

  // General
  showCaseRoadmap: boolean
  onToggleCaseRoadmap: (enabled: boolean) => void
  showStructuredIngest: boolean
  onToggleStructuredIngest: (enabled: boolean) => void

  // Extensions
  extensions: ExtensionRow[]
  onToggleExtension: (id: string, next: boolean) => void

  // Extraction
  showInternalNodes: boolean
  onToggleInternalNodes: (enabled: boolean) => void

  // Agent context
  savedAgentContext: string
  onSaveAgentContext: (context: string) => void

  // Reset
  onResetCase: () => void

  // Deep-agent caps
  savedCaps: CapsDraft
  onSaveCaps: (caps: CapsDraft) => void
}

function FieldHeader({ label, description }: { label: string; description: string }) {
  return (
    <div className="space-y-1">
      <Label className="text-sm font-medium text-foreground">{label}</Label>
      <p className="text-xs leading-relaxed text-muted-foreground">{description}</p>
    </div>
  )
}

interface SettingsCardProps {
  icon: React.ReactNode
  title: string
  description: string
  /** Visually distinguish high-emphasis cards (e.g. the deep agent card)
   *  with a primary-tinted icon disc and a subtle accent border. */
  accent?: boolean
  children: React.ReactNode
}

/** A discrete settings group with a clear identity — icon disc, title,
 *  one-line description, then body. Use one per logical concern. */
function SettingsCard({ icon, title, description, accent, children }: SettingsCardProps) {
  return (
    <Card
      asChild
      tone={accent ? 'primary' : 'neutral'}
      className={accent ? 'rounded-xl border-primary/30 p-0' : 'rounded-xl p-0'}
    >
      <section>
        <header className="flex items-start gap-3 border-b border-border px-5 py-4">
          <div
            className={
              accent
                ? 'flex size-9 shrink-0 items-center justify-center rounded-lg border border-primary/40 bg-primary/10 text-primary'
                : 'flex size-9 shrink-0 items-center justify-center rounded-lg border border-border bg-muted/40 text-muted-foreground'
            }
          >
            {icon}
          </div>
          <div className="min-w-0 space-y-0.5">
            <h2 className="text-sm font-semibold tracking-tight text-foreground">{title}</h2>
            <p className="text-xs leading-relaxed text-muted-foreground">{description}</p>
          </div>
        </header>
        {children}
      </section>
    </Card>
  )
}

interface CapInputProps {
  label: string
  hint: string
  value: number
  onChange: (next: number) => void
  min: number
  max: number
  step?: number
  suffix?: string
  disabled?: boolean
}

function CapInput({
  label,
  hint,
  value,
  onChange,
  min,
  max,
  step = 1,
  suffix,
  disabled,
}: CapInputProps) {
  // Local string state lets the user clear the input mid-edit without
  // snapping to NaN. We commit on blur.
  const [draft, setDraft] = useState(String(value))
  useEffect(() => {
    setDraft(String(value))
  }, [value])

  const commit = () => {
    const parsed = Number.parseInt(draft, 10)
    if (Number.isFinite(parsed)) {
      const clamped = Math.min(max, Math.max(min, parsed))
      onChange(clamped)
      setDraft(String(clamped))
    } else {
      setDraft(String(value))
    }
  }

  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-medium text-foreground">
        {label}
        <span className="ml-1 text-muted-foreground/70">
          ({min}–{max})
        </span>
      </Label>
      <div className="flex items-center gap-2">
        <Input
          type="number"
          inputMode="numeric"
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onBlur={commit}
          min={min}
          max={max}
          step={step}
          disabled={disabled}
          className="w-full"
        />
        {suffix && (
          <span className="text-xs text-muted-foreground whitespace-nowrap max-md:min-w-14">
            {suffix}
          </span>
        )}
      </div>
      <p className="text-2xs leading-snug text-muted-foreground">{hint}</p>
    </div>
  )
}

interface CaseExtensionsSectionProps {
  extensions: ExtensionRow[]
  onToggle: (id: string, next: boolean) => void
}

/** Lists every globally-enabled extension with a toggle (user intent) and a
 *  separate health indicator (service reality). Renders nothing when no
 *  extension is globally enabled. */
function CaseExtensionsSection({ extensions, onToggle }: CaseExtensionsSectionProps) {
  const { t } = useTranslation()
  if (extensions.length === 0) return null

  return (
    <SettingsCard
      icon={<Puzzle className="size-4" />}
      title={t('extensions.case_settings.title')}
      description={t('extensions.case_settings.description')}
    >
      <div className="divide-y divide-border">
        {extensions.map(ext => (
          <div key={ext.id} className="flex items-start justify-between gap-4 p-5">
            <div className="min-w-0 space-y-1">
              <Label className="text-sm font-medium text-foreground">{ext.name}</Label>
              <p className="text-xs leading-relaxed text-muted-foreground">{ext.description}</p>
              <div className="pt-1">
                <HealthDot status={ext.status} />
              </div>
            </div>
            {/* Switch is the user intent. Stays operable when the service is unreachable. */}
            <Switch
              checked={ext.checked}
              onCheckedChange={next => onToggle(ext.id, next)}
              className="mt-0.5 shrink-0"
            />
          </div>
        ))}
      </div>
    </SettingsCard>
  )
}

/**
 * Pure presentation + local draft state for the case-settings page. All
 * persistence (settings mutation, reset, toasts) lives in the connected
 * wrapper in `features/case-overview`, surfaced here through callbacks.
 */
export function CaseSettingsView({
  loading,
  saving,
  resetting,
  showCaseRoadmap,
  onToggleCaseRoadmap,
  showStructuredIngest,
  onToggleStructuredIngest,
  extensions,
  onToggleExtension,
  showInternalNodes,
  onToggleInternalNodes,
  savedAgentContext,
  onSaveAgentContext,
  onResetCase,
  savedCaps,
  onSaveCaps,
}: CaseSettingsViewProps) {
  const { t } = useTranslation()
  const [agentContextDraft, setAgentContextDraft] = useState(savedAgentContext)
  const agentContextChanged = agentContextDraft !== savedAgentContext

  // Deep-agent caps — local draft state for the four numeric inputs.
  const [capsDraft, setCapsDraft] = useState<CapsDraft>(savedCaps)
  const capsChanged =
    capsDraft.step_cap !== savedCaps.step_cap ||
    capsDraft.wall_clock_seconds !== savedCaps.wall_clock_seconds ||
    capsDraft.max_tokens !== savedCaps.max_tokens ||
    capsDraft.max_findings !== savedCaps.max_findings

  useEffect(() => {
    setAgentContextDraft(savedAgentContext)
  }, [savedAgentContext])

  // Re-init caps draft when the saved values arrive / change.
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-init only on saved-value change
  useEffect(() => {
    setCapsDraft(savedCaps)
  }, [
    savedCaps.step_cap,
    savedCaps.wall_clock_seconds,
    savedCaps.max_tokens,
    savedCaps.max_findings,
  ])

  if (loading) {
    return (
      <Card className="max-w-xl p-6">
        <div className="flex items-center gap-2">
          <Loader2 className="size-4 animate-spin text-muted-foreground" />
          <span className="text-sm text-muted-foreground">{t('cases.settings.loading')}</span>
        </div>
      </Card>
    )
  }

  return (
    <div className="flex flex-col gap-4">
      {/* General settings — display / UX preferences for this case. Sits at the
          top so it's the first thing users see. */}
      <SettingsCard
        icon={<Settings2 className="size-4" />}
        title={t('cases.settings.general.title')}
        description={t('cases.settings.general.description')}
      >
        <div className="divide-y divide-border">
          {/* Show Case Roadmap */}
          <div className="p-5 flex items-start justify-between gap-4">
            <FieldHeader
              label={t('cases.settings.general.roadmapLabel')}
              description={t('cases.settings.general.roadmapDescription')}
            />
            <Switch
              checked={showCaseRoadmap}
              onCheckedChange={onToggleCaseRoadmap}
              disabled={saving}
              className="mt-0.5 shrink-0"
            />
          </div>
          {/* Show Structured ingest in the case menu */}
          <div className="p-5 flex items-start justify-between gap-4">
            <FieldHeader
              label={t('cases.settings.general.structuredIngestLabel')}
              description={t('cases.settings.general.structuredIngestDescription')}
            />
            <Switch
              checked={showStructuredIngest}
              onCheckedChange={onToggleStructuredIngest}
              disabled={saving}
              className="mt-0.5 shrink-0"
            />
          </div>
        </div>
      </SettingsCard>

      <CaseExtensionsSection extensions={extensions} onToggle={onToggleExtension} />

      <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-2">
        <SettingsCard
          icon={<FileScan className="size-4" />}
          title={t('cases.settings.extraction.title')}
          description={t('cases.settings.extraction.description')}
        >
          <div className="divide-y divide-border">
            {/* Show Source Nodes */}
            <div className="p-5 flex items-start justify-between gap-4">
              <FieldHeader
                label={t('cases.settings.extraction.sourceNodesLabel')}
                description={t('cases.settings.extraction.sourceNodesDescription')}
              />
              <Switch
                checked={showInternalNodes}
                onCheckedChange={onToggleInternalNodes}
                disabled={saving}
                className="mt-0.5 shrink-0"
              />
            </div>

            {/* Additional Case Context for AI */}
            <div className="p-5 space-y-3">
              <FieldHeader
                label={t('cases.settings.extraction.aiContextLabel')}
                description={t('cases.settings.extraction.aiContextDescription')}
              />
              <Textarea
                value={agentContextDraft}
                onChange={e => setAgentContextDraft(e.target.value)}
                placeholder={t('cases.settings.extraction.aiContextPlaceholder')}
                className="min-h-32 max-h-[33vh] resize-y overflow-y-auto text-base md:text-sm"
                disabled={saving}
              />
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  className="max-md:h-9"
                  onClick={() => onSaveAgentContext(agentContextDraft)}
                  disabled={saving || !agentContextChanged}
                >
                  {t('cases.settings.extraction.saveAiContext')}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="max-md:h-9"
                  onClick={() => setAgentContextDraft(savedAgentContext)}
                  disabled={saving || !agentContextChanged}
                >
                  {t('common.reset')}
                </Button>
                {saving && <Loader2 className="size-4 animate-spin text-muted-foreground" />}
              </div>
            </div>

            {/* Reset Extractions — stacked on the phone so the warning copy keeps the full width. */}
            <div className="p-5 flex flex-col items-start gap-3 md:flex-row md:justify-between md:gap-4">
              <div className="space-y-1 min-w-0">
                <Label className="text-sm font-medium text-destructive">
                  {t('cases.settings.reset.label')}
                </Label>
                <p className="text-xs leading-relaxed text-muted-foreground">
                  {t('cases.settings.reset.description')}
                </p>
              </div>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="shrink-0 text-destructive border-destructive/40 hover:bg-destructive/10 hover:text-destructive max-md:h-9"
                    disabled={resetting}
                  >
                    {resetting ? (
                      <Loader2 className="size-3.5 animate-spin mr-1.5" />
                    ) : (
                      <RotateCcw className="size-3.5 mr-1.5" />
                    )}
                    {t('common.reset')}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>{t('cases.settings.reset.dialogTitle')}</AlertDialogTitle>
                    <AlertDialogDescription className="whitespace-pre-line">
                      {t('cases.settings.reset.dialogDescription')}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={onResetCase}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    >
                      {t('cases.settings.reset.confirm')}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        </SettingsCard>

        <SettingsCard
          icon={<FlaskConical className="size-4" />}
          title={t('cases.settings.agent.title')}
          description={t('cases.settings.agent.description')}
        >
          <div className="space-y-5 p-5">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <CapInput
                label={t('cases.settings.agent.toolCallsLabel')}
                hint={t('cases.settings.agent.toolCallsHint')}
                value={capsDraft.step_cap}
                onChange={v => setCapsDraft(s => ({ ...s, step_cap: v }))}
                min={5}
                max={200}
                suffix={t('cases.settings.agent.toolCallsSuffix')}
                disabled={saving}
              />
              <CapInput
                label={t('cases.settings.agent.wallClockLabel')}
                hint={t('cases.settings.agent.wallClockHint')}
                value={capsDraft.wall_clock_seconds}
                onChange={v => setCapsDraft(s => ({ ...s, wall_clock_seconds: v }))}
                min={60}
                max={3600}
                suffix={t('cases.settings.agent.wallClockSuffix')}
                disabled={saving}
              />
              <CapInput
                label={t('cases.settings.agent.outputTokensLabel')}
                hint={t('cases.settings.agent.outputTokensHint')}
                value={capsDraft.max_tokens}
                onChange={v => setCapsDraft(s => ({ ...s, max_tokens: v }))}
                min={1024}
                max={32768}
                step={256}
                suffix={t('cases.settings.agent.outputTokensSuffix')}
                disabled={saving}
              />
              <CapInput
                label={t('cases.settings.agent.findingsLabel')}
                hint={t('cases.settings.agent.findingsHint')}
                value={capsDraft.max_findings}
                onChange={v => setCapsDraft(s => ({ ...s, max_findings: v }))}
                min={1}
                max={30}
                suffix={t('cases.settings.agent.findingsSuffix')}
                disabled={saving}
              />
            </div>

            <div className="flex items-center gap-2 border-t border-border pt-4">
              <Button
                size="sm"
                className="max-md:h-9"
                onClick={() => onSaveCaps(capsDraft)}
                disabled={saving || !capsChanged}
              >
                {t('cases.settings.agent.saveLimits')}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="max-md:h-9"
                onClick={() => setCapsDraft(savedCaps)}
                disabled={saving || !capsChanged}
              >
                {t('common.reset')}
              </Button>
              {saving && <Loader2 className="size-4 animate-spin text-muted-foreground" />}
            </div>
          </div>
        </SettingsCard>
      </div>
    </div>
  )
}
